import { CommonModule } from '@angular/common';
import { Component, effect, forwardRef, input, InputSignal, output } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { WeissmanDatetimeComponent } from '../../../../UI-Lib/Weiss-Datepicker/weissman-datetime.component';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { EngagementNumberLookupModalComponent } from 'src/app/Common/Contracts-Invoices/engagement-number-lookup-modal.component';
import { Engagement } from 'src/app/Common/Contracts-Invoices/contract-invoices.model';
import { ReferralPageService } from '../referralPage.service';
import { CurrencyMaskModule } from 'src/app/UI-Lib/Currency/Mask/currencyMask.module';
import { RevertableState } from './revertableState';
import { parseBooleanString, toNumberOrNull, toPlainNumberString } from './referralInputUtils';
import { UserPickerComponent } from '../../../../Common/User-Pickers/user.picker.component';

type ReferralInputValue = string | number | Date | boolean | System.Guid | null | undefined;

@Component({
    selector: 'referral-input-with-revert',
    imports: [ReactiveFormsModule, FormsModule, CommonModule, WeissmanDatetimeComponent, CurrencyMaskModule, UserPickerComponent],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ReferralInputWithRevertComponent),
            multi: true,
        },
    ],
    templateUrl: './referralInputWithRevert.component.html',
    styles: [
        `
            .input-with-revert {
                min-width: 0;
            }

            .input-with-revert__control {
                flex: 1 1 auto;
                min-width: 0;
            }

            .input-with-revert__button {
                flex: 0 0 auto;
            }

            .differs-from-system {
                color: blue;
            }
        `,
    ],
})
export class ReferralInputWithRevertComponent implements ControlValueAccessor {
    constructor(
        private readonly _modalService: BsModalService,
        private readonly _referralPageService: ReferralPageService,
    ) {
        effect(() => {
            this._state.setSystemValue(this.systemValue());
        });
        effect(() => {
            this._state.setOriginalValue(this.originalValue());
        });
    }

    systemValue: InputSignal<ReferralInputValue> = input<ReferralInputValue>(null);
    originalValue: InputSignal<ReferralInputValue> = input<ReferralInputValue>(null);
    isDate: InputSignal<boolean> = input<boolean>(false);
    isNumber: InputSignal<boolean> = input<boolean>(false);
    isBoolean: InputSignal<boolean> = input<boolean>(false);
    isPercent: InputSignal<boolean> = input<boolean>(false);
    isCurrency: InputSignal<boolean> = input<boolean>(false);
    isTextArea: InputSignal<boolean> = input<boolean>(false);
    hasUnknown: InputSignal<boolean> = input<boolean>(false);
    isEngagementNumber: InputSignal<boolean> = input<boolean>(false);
    isUserPicker: InputSignal<boolean> = input<boolean>(false);
    userDisplayValue: InputSignal<string> = input<string>('');
    systemUserDisplayValue: InputSignal<string> = input<string>('');
    originalUserDisplayValue: InputSignal<string> = input<string>('');
    selectOptions: InputSignal<Weissman.Model.NameValuePair[]> = input<Weissman.Model.NameValuePair[]>([]);
    placeholder: InputSignal<string> = input<string>('');
    maxlength: InputSignal<number> = input<number>(null);
    precision: InputSignal<number> = input<number>(0);
    customWidth: InputSignal<number> = input<number>(null);

    editMode = this._referralPageService.editMode;
    workflowStatus = this._referralPageService.workflowStatus;

    engagementDataLoaded = output<string>();
    engagementDataReverted = output<boolean>();
    userDisplayValueChange = output<string>();

    bsModalRef: BsModalRef;

    private readonly _state = new RevertableState<ReferralInputValue>();
    private _percentRawInput: string | null = null;

    onChange: (value: ReferralInputValue) => void = () => {};
    onTouched: () => void = () => {};

    get value(): ReferralInputValue {
        return this._state.value;
    }

    get textValue(): string {
        return this.value == null ? '' : String(this.value);
    }

    get dateValue(): Date | null {
        return this.value instanceof Date ? this.value : null;
    }

    get numberValue(): string | number | null {
        if (
            this.value instanceof Date ||
            typeof this.value === 'boolean' ||
            (typeof this.value !== 'string' && typeof this.value !== 'number')
        ) {
            return null;
        }
        return this.value as string | number | null;
    }

    get numberDigitsInfo(): string {
        if (this.numberValue == null || this.numberValue === '') {
            return '1.0-0';
        }

        const decimalPart = String(this.numberValue).split('.')[1] ?? '';
        const decimalPlaces = decimalPart.length;
        return `1.${decimalPlaces}-${decimalPlaces}`;
    }

    get percentInputValue(): string {
        if (this._percentRawInput != null) {
            return this._percentRawInput;
        }

        if (this.numberValue == null || this.numberValue === '') {
            return '';
        }

        return toPlainNumberString(this.numberValue);
    }

    get booleanValue(): boolean | null {
        return typeof this.value === 'boolean' ? this.value : null;
    }

    get selectValue(): string {
        const currentValue = this.value == null ? '' : String(this.value);
        const option = this.selectOptions().find((o) => String(o.value ?? '') === currentValue);
        return option ? option.name : '';
    }

    get userPickerContact(): Core.ContactModel {
        const displayName = this.userDisplayValue();
        const userId = this.value as System.Guid;

        return displayName ? ({ userID: userId, firstName: displayName, lastName: '' } as Core.ContactModel) : null;
    }

    get valueDiffersFromSystem(): boolean {
        if (this.isUserPicker()) {
            return this._userValueDiffersFrom(this.systemValue(), this.systemUserDisplayValue());
        }

        return this._state.valueDiffersFromSystem;
    }

    get valueDiffersFromOriginal(): boolean {
        if (this.isUserPicker()) {
            return this._userValueDiffersFrom(
                this._state.effectiveOriginalValue,
                this._userDisplayForEffectiveOriginal(),
            );
        }

        return this._state.valueDiffersFromOriginal;
    }

    get overriddenClass(): string {
        if (this.valueDiffersFromOriginal) {
            return this.isDate() && this.editMode() ? 'date-time-override' : 'overridden';
        }

        if (this.valueDiffersFromSystem) {
            return this.isDate() && this.editMode() ? 'date-time-system-override' : 'differs-from-system';
        }

        return '';
    }

    onTextInput(rawValue: string | number | null): void {
        let value: ReferralInputValue = rawValue;
        if (this.isNumber() || this.isPercent()) {
            value = rawValue == null ? null : toNumberOrNull(String(rawValue));
            if (!this.isPercent()) {
                this._percentRawInput = null;
            }
        } else if (this.isBoolean()) {
            value = parseBooleanString(rawValue);
            this._percentRawInput = null;
        } else {
            this._percentRawInput = null;
        }

        this._setValue(value);
    }

    onPercentInput(rawValue: string | number | null): void {
        this._percentRawInput = rawValue == null ? '' : String(rawValue);
        this._setValue(rawValue == null ? null : toNumberOrNull(String(rawValue)));
    }

    onDateInput(value: Date): void {
        this._percentRawInput = null;
        this._setValue(value);
    }

    revertToSystem(): void {
        this._percentRawInput = null;
        this._setValue(this._state.revertToSystem());
        if (this.isEngagementNumber()) {
            this.engagementDataReverted.emit(true);
        }
        if (this.isUserPicker()) {
            this.userDisplayValueChange.emit(this.systemUserDisplayValue() ?? '');
        }
    }

    revertToOriginal(): void {
        this._percentRawInput = null;
        const originalValue = this._state.revertToOriginal();
        const useSystemUser = this.isUserPicker() && this._shouldUseSystemUserForOriginalRevert(originalValue);
        this._setValue(useSystemUser ? this.systemValue() : originalValue);
        if (this.isEngagementNumber()) {
            this.engagementDataReverted.emit(false);
        }
        if (this.isUserPicker()) {
            const displayValue = useSystemUser ? this.systemUserDisplayValue() : this.originalUserDisplayValue();
            this.userDisplayValueChange.emit(displayValue ?? '');
        }
    }

    onUserSelected(contact: Core.ContactModel): void {
        const userId = contact?.userID ?? null;
        const displayName = contact ? [contact.firstName, contact.lastName].filter(Boolean).join(' ') : '';

        this.userDisplayValueChange.emit(displayName);
        this._setValue(userId);
        this.onTouched();
    }

    clearUser(): void {
        this.userDisplayValueChange.emit('');
        this._setValue(null);
        this.onTouched();
    }

    lookupEngagement(): void {
        this.bsModalRef = this._modalService.show(EngagementNumberLookupModalComponent, { class: 'modal-lg' });
        this.bsModalRef.content.title = 'Lookup Engagement Number';

        this.bsModalRef.content.onClose = async (engagement: Engagement) => {
            this._percentRawInput = null;
            this._setValue(engagement.engagementID);
            this.engagementDataLoaded.emit(engagement.engagementID);
        };
    }

    writeValue(value: ReferralInputValue): void {
        this._percentRawInput = null;
        this._state.value = value;
        this._state.setOriginalValue(this.originalValue());
    }

    registerOnChange(fn: (value: ReferralInputValue) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    private _setValue(value: ReferralInputValue): void {
        this._state.value = value;
        this.onChange(value);
    }

    private _userValueDiffersFrom(referenceValue: ReferralInputValue, referenceDisplayValue: string): boolean {
        const currentUserId = this._normalizeUserId(this.value);
        const referenceUserId = this._normalizeUserId(referenceValue);

        if (currentUserId && referenceUserId) {
            return currentUserId !== referenceUserId;
        }

        return this._normalizeUserDisplay(this.userDisplayValue()) !== this._normalizeUserDisplay(referenceDisplayValue);
    }

    private _userDisplayForEffectiveOriginal(): string {
        return this._userValuesAreEqual(
            this._state.effectiveOriginalValue,
            this.originalUserDisplayValue(),
            this.systemValue(),
            this.systemUserDisplayValue(),
        )
            ? this.systemUserDisplayValue()
            : this.originalUserDisplayValue();
    }

    private _shouldUseSystemUserForOriginalRevert(originalValue: ReferralInputValue): boolean {
        if (this._normalizeUserId(originalValue)) {
            return false;
        }

        const systemUserId = this._normalizeUserId(this.systemValue());

        return (
            !!systemUserId &&
            this._normalizeUserDisplay(this.originalUserDisplayValue()) ===
                this._normalizeUserDisplay(this.systemUserDisplayValue())
        );
    }

    private _userValuesAreEqual(
        left: ReferralInputValue,
        leftDisplayValue: string,
        right: ReferralInputValue,
        rightDisplayValue: string,
    ): boolean {
        const leftUserId = this._normalizeUserId(left);
        const rightUserId = this._normalizeUserId(right);

        if (leftUserId && rightUserId) {
            return leftUserId === rightUserId;
        }

        return this._normalizeUserDisplay(leftDisplayValue) === this._normalizeUserDisplay(rightDisplayValue);
    }

    private _normalizeUserId(value: ReferralInputValue): string {
        return value == null ? '' : String(value).trim().toLowerCase();
    }

    private _normalizeUserDisplay(value: string): string {
        return (value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
    }
}
